import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { useInquirySettings } from "@/lib/inquiry";
import { Pick } from "@/components/inquiries/InquiryFormDialog";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40) || `x_${Date.now().toString(36)}`;

const Card = ({ title, hint, children, onAdd, testid }) => (
  <section className="bg-white border border-slate-200 rounded-md p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-1"><h2 className="text-sm font-semibold">{title}</h2>
      {onAdd && <Button size="sm" variant="ghost" onClick={onAdd} data-testid={`${testid}-add`} className="h-7 gap-1 text-xs text-blue-700"><Plus className="w-3 h-3" />Add</Button>}</div>
    {hint && <p className="text-xs text-slate-500 mb-3">{hint}</p>}
    <div className="space-y-1.5">{children}</div>
  </section>
);

const ListEditor = ({ title, hint, items, onChange, testid }) => (
  <Card title={title} hint={hint} testid={testid} onAdd={() => onChange([...items, ""])}>
    {items.map((v, i) => (
      <div key={i} className="flex gap-1.5">
        <Input data-testid={`${testid}-item-${i}`} value={v} maxLength={60} onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} className="h-8" />
        <Button size="icon" variant="ghost" data-testid={`${testid}-del-${i}`} onClick={() => onChange(items.filter((_, j) => j !== i))} className="h-8 w-8 text-red-600"><Trash2 className="w-4 h-4" /></Button>
      </div>
    ))}
  </Card>
);

const KINDS = [{ value: "open", label: "Open" }, { value: "converted", label: "Converted" }, { value: "closed", label: "Closed" }];
const TYPES = ["text", "number", "date", "select", "boolean"].map((t) => ({ value: t, label: t === "boolean" ? "Yes / No" : t[0].toUpperCase() + t.slice(1) }));

export default function InquirySettings() {
  const [saved, reload] = useInquirySettings();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (saved) setS(JSON.parse(JSON.stringify(saved))); }, [saved]);
  if (!s) return <div className="text-sm text-slate-500">Loading…</div>;
  const set = (k) => (v) => setS((x) => ({ ...x, [k]: v }));
  const upd = (k, i, patch) => setS((x) => ({ ...x, [k]: x[k].map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...s, custom_fields: s.custom_fields.map((f) => ({ ...f, key: f.key || slug(f.label), options: f.type === "select" ? f.options : [] })),
        statuses: s.statuses.map((st) => ({ ...st, key: st.key || slug(st.label) })) };
      await api.put("/inquiry-settings", body); toast.success("Inquiry settings saved"); reload();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="inquiry-settings-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div><div className="text-[11px] uppercase tracking-wider text-slate-500">Inquiries</div><h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Inquiry settings</h1>
          <p className="text-sm text-slate-500 mt-1">Shape the module for client acquisition, vendor quotations, procurement or family-office work.</p></div>
        <Button data-testid="inq-settings-save" disabled={busy} onClick={save} className="bg-blue-700 hover:bg-blue-800 text-white gap-2"><Save className="w-4 h-4" />{busy ? "Saving…" : "Save settings"}</Button>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
        <Card title="Statuses" testid="set-statuses" hint="Each status belongs to Open, Converted or Closed. Keep at least one of each." onAdd={() => set("statuses")([...s.statuses, { key: "", label: "New status", kind: "open" }])}>
          {s.statuses.map((st, i) => (
            <div key={i} className="flex gap-1.5">
              <Input data-testid={`set-status-label-${i}`} value={st.label} maxLength={40} onChange={(e) => upd("statuses", i, { label: e.target.value })} className="h-8" />
              <div className="w-32"><Pick testid={`set-status-kind-${i}`} value={st.kind} onChange={(v) => upd("statuses", i, { kind: v })} options={KINDS} /></div>
              <Button size="icon" variant="ghost" onClick={() => set("statuses")(s.statuses.filter((_, j) => j !== i))} className="h-8 w-8 text-red-600"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </Card>
        <ListEditor testid="set-categories" title="Categories" items={s.categories} onChange={set("categories")} />
        <ListEditor testid="set-channels" title="Sources / channels" items={s.channels} onChange={set("channels")} />
        <ListEditor testid="set-interactions" title="Interaction types" hint="Used for journey entries." items={s.interaction_types} onChange={set("interaction_types")} />
        <ListEditor testid="set-closure" title="Closure reasons" items={s.closure_reasons} onChange={set("closure_reasons")} />
        <Card title="Conversion destinations" testid="set-conversion">
          {[["task", "Task"], ["obligation", "Obligation"]].map(([k, l]) => (
            <label key={k} className="flex items-center justify-between text-sm border border-slate-200 rounded-md px-3 py-2">{l}
              <Switch data-testid={`set-conv-${k}`} checked={s.conversion_targets.includes(k)} onCheckedChange={(v) => set("conversion_targets")(v ? [...s.conversion_targets, k] : s.conversion_targets.filter((x) => x !== k))} /></label>
          ))}
        </Card>
        <div className="md:col-span-2 xl:col-span-3">
          <Card title="Custom fields" testid="set-custom" hint="Extra fields on every inquiry, e.g. Budget, Expected start, Referral partner." onAdd={() => set("custom_fields")([...s.custom_fields, { key: "", label: "", type: "text", options: [], required: false }])}>
            {s.custom_fields.length === 0 && <div className="text-xs text-slate-400">No custom fields.</div>}
            {s.custom_fields.map((f, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <Input data-testid={`set-cf-label-${i}`} placeholder="Field name" value={f.label} maxLength={60} onChange={(e) => upd("custom_fields", i, { label: e.target.value })} className="h-8 w-56" />
                <div className="w-36"><Pick testid={`set-cf-type-${i}`} value={f.type} onChange={(v) => upd("custom_fields", i, { type: v })} options={TYPES} /></div>
                {f.type === "select" && <Input data-testid={`set-cf-options-${i}`} placeholder="Options, comma separated" value={f.options.join(", ")} onChange={(e) => upd("custom_fields", i, { options: e.target.value.split(",").map((x) => x.trim()) })} className="h-8 flex-1 min-w-[200px]" />}
                <label className="text-xs text-slate-600 flex items-center gap-1.5"><Switch data-testid={`set-cf-required-${i}`} checked={f.required} onCheckedChange={(v) => upd("custom_fields", i, { required: v })} />Required</label>
                <Button size="icon" variant="ghost" onClick={() => set("custom_fields")(s.custom_fields.filter((_, j) => j !== i))} className="h-8 w-8 text-red-600"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}
